<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { mdiChevronDown, mdiChevronUp, mdiOpenInNew } from '@mdi/js'
import Icon from '@/components/Icon.vue'
import AsideMenuList from '@/components/AsideMenuList.vue'

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  isSubmenuList: Boolean,
  isCollapsed: Boolean
})

const emit = defineEmits(['menu-click'])

const route = useRoute()
const store = useStore()
const { t } = useI18n()

/* Menu entries carry a `labelKey`, not a label: menu.js is a plain module and
   cannot translate anything, and resolving here means a language change
   re-labels the sidebar reactively instead of on the next reload. `label` is
   still honoured so a caller can pass literal text. */
const label = computed(() => props.item.labelKey ? t(props.item.labelKey) : props.item.label)

/* A pure group — `{ label, icon, menu }` with neither `to` nor `href` — used to
   fall through to `<a>`, which then rendered with a null href. An anchor with
   no href is not in the tab order and does not activate on Enter or Space, so
   the two collapsible groups in menu.js would have been mouse-only. They are
   buttons, which is what a control that only toggles something always was. */
const componentIs = computed(() => {
  if (props.item.to) return 'router-link'
  return props.item.href ? 'a' : 'button'
})

const isButton = computed(() => componentIs.value === 'button')

const hasDropdown = computed(() => !!props.item.menu)

const itemTo = computed(() => props.item.to || null)

const itemHref = computed(() => props.item.href || null)

const itemTarget = computed(() => componentIs.value === 'a' && props.item.target ? props.item.target : null)

/* Menu entries can point off-site (the Affiliates portal). Those must not leak
   this app as the Referer — see the document-level policy in index.html; this
   is the per-link guarantee for a tag built dynamically rather than written
   out as a literal <a> in a template. */
const itemRel = computed(() => (componentIs.value === 'a' && props.item.href ? 'noopener noreferrer' : null))
const itemReferrerPolicy = computed(() => (componentIs.value === 'a' && props.item.href ? 'no-referrer' : null))

/* The anchor-only attributes, bound as ONE object so that on a router-link the
   keys are absent rather than null. Bound one by one, `href: null` fell through
   onto RouterLink's own <a> and overwrote the href it had just computed: every
   sidebar route rendered as an <a> with no href — clickable, but out of the Tab
   order and not a link to assistive technology. */
const anchorAttrs = computed(() => (componentIs.value === 'a'
  ? { href: itemHref.value, target: itemTarget.value, rel: itemRel.value, referrerpolicy: itemReferrerPolicy.value }
  : {}))

const isExternal = computed(() => !!props.item.href && props.item.target === '_blank')

/* Whether the current route belongs to `item`: its own page, or one of the
   routes its `match` list claims (menu.js). An entry ending in "/" claims the
   whole subtree; anything else has to match exactly. Before this only an exact
   path counted, so /logs/42, /replenish or /websites/7/subscription lit up
   nothing and left their group shut. */
const belongsTo = (item, path) => {
  if (item.to && path === item.to) return true
  return Array.isArray(item.match) &&
    item.match.some(entry => (entry.endsWith('/') ? path.startsWith(entry) : path === entry))
}

const isExact = computed(() => !!props.item.to && route.path === props.item.to)

// A collapsed child inside an open group still counts: keep the parent lit.
const hasActiveChild = computed(() =>
  Array.isArray(props.item.menu) &&
  props.item.menu.some(child => belongsTo(child, route.path))
)

/* A child that claims the route outranks its parent's `match`, so on
   /campaigns/chart it is "Campaign Chart" that is current, and "Campaigns" is
   only lit as the group holding it — never two current items at once. */
const isActive = computed(() =>
  isExact.value || (!hasActiveChild.value && belongsTo(props.item, route.path))
)

/* "page" for the page itself; "true" for a detail page inside the section the
   item stands for (a campaign's logs under Campaigns), which is the value ARIA
   has for "current item in a set" when the link is not literally this page. */
const ariaCurrent = computed(() => {
  if (isExact.value) return 'page'
  return isActive.value ? 'true' : null
})

const isDropdownActive = ref(hasActiveChild.value)

/* Seeding the ref only covers the first render. The sidebar is part of the
   persistent layout, so navigating to a child from somewhere else in the app —
   the dashboard's "Per campaign" link, for instance — never remounts this item
   and would leave the group shut while the page it points at is on screen.
   Opening on arrival fixes that; it deliberately does not close again on leave,
   so a group the user opened by hand stays open as they move around. */
watch(hasActiveChild, (active) => {
  if (active) isDropdownActive.value = true
})

const dropdownIcon = computed(() => isDropdownActive.value ? mdiChevronUp : mdiChevronDown)

const isHighlighted = computed(() => isActive.value || (hasDropdown.value && hasActiveChild.value))

const linkClass = computed(() => {
  const base = [
    'group relative flex items-center rounded-xl mx-2 transition-colors duration-150',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500'
  ]

  /* A <button> brings its own UA styling — centred text, a border, `background:
     buttonface` and a font it does not inherit — none of which the anchor
     needed. Width is the subtle one, and it is handled in the scoped CSS below
     rather than with `w-full`: measured in the browser, `w-full` renders 239px
     against the links' 223px, because 100% resolves against the <li> and
     ignores this row's own `mx-2`. */
  if (isButton.value) {
    base.push('aside-group-btn appearance-none bg-transparent border-0 text-left cursor-pointer')
  }

  if (props.isCollapsed) {
    base.push('justify-center gap-0 px-0 py-2.5')
  } else {
    /* Every row here is a tap target in the mobile drawer, so all of them clear
       the 44px touch minimum — they were ~36px (rows) and ~30px (submenu rows).
       Not gated behind `lg:`: the sidebar is still a touch-driven overlay all
       the way to xl.
       Both tiers are `text-sm`. A submenu row used to be `text-[13px]`, which
       the type-scale pass retired along with the other 42 arbitrary sizes — one
       pixel was never what distinguished a child row anyway. The `ml-6` indent,
       the guide rail down the left of the nested list and the smaller icon
       (17px against 20px) all still do, and they read at a glance where 1px of
       type never did. */
    base.push(
      'gap-3 min-h-[44px] text-sm',
      props.isSubmenuList ? 'py-2.5 px-3' : 'py-3 px-3'
    )
  }

  base.push(
    isHighlighted.value
      ? 'bg-brand-50 text-brand-700 font-semibold dark:bg-brand-500 dark:bg-opacity-15 dark:text-brand-300'
      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100'
  )

  return base
})

/* Whether this item's children are shown while the sidebar is a 4rem icon rail.
   For a parent that is also a link (Campaigns) they are: the chevron is hidden
   at that width, so gating them would leave the child route reachable only by
   URL, and one extra icon under its parent reads fine.
   For a pure group it is the opposite — Settings and Billing hold seven rows
   between them, and spilling all of them into the rail turns it into eleven
   unlabelled icons, which is the thing collapsing is for. Those stay shut, and
   clicking one widens the sidebar so the group can be read. */
const showChildrenWhileCollapsed = computed(() => !!props.item.to)

/* Deliberately ignores `isDropdownActive` while collapsed, rather than OR-ing
   with it. A group the user had opened at full width keeps that flag set when
   the sidebar is collapsed afterwards — and an OR let its children straight
   back into the rail, which is the exact spill this is meant to prevent
   (verified in the browser: collapsing with both groups open put eleven
   unlabelled icons in a 4rem strip and made it scroll). The flag is not
   cleared, so expanding again restores whatever was open. */
const childrenVisible = computed(() =>
  props.isCollapsed ? showChildrenWhileCollapsed.value : isDropdownActive.value
)

const menuClick = event => {
  emit('menu-click', event, props.item)

  if (!hasDropdown.value) return

  // Icon rail + pure group: widen first, then open. `asideCollapseToggle` takes
  // no payload, and we only get here while collapsed, so it expands.
  if (props.isCollapsed && !showChildrenWhileCollapsed.value) {
    store.dispatch('asideCollapseToggle')
    isDropdownActive.value = true
    return
  }

  isDropdownActive.value = !isDropdownActive.value
}
</script>

<template>
  <li>
    <component
      :is="componentIs"
      :to="itemTo"
      v-bind="anchorAttrs"
      :type="isButton ? 'button' : null"
      :class="linkClass"
      :aria-current="ariaCurrent"
      :aria-expanded="hasDropdown ? String(isDropdownActive) : null"
      :title="isCollapsed ? label : null"
      :aria-label="isCollapsed ? label : null"
      @click="menuClick"
    >
      <!-- Brand rail marks the active route without relying on the tint alone. -->
      <span
        v-if="isActive"
        class="aside-rail absolute top-1.5 bottom-1.5 w-1 bg-brand-600 dark:bg-brand-400"
        aria-hidden="true"
      />
      <icon
        v-if="item.icon"
        :path="item.icon"
        :size="isCollapsed || !isSubmenuList ? 20 : 18"
        class="flex-none"
        :w="isCollapsed ? 'w-5' : (isSubmenuList ? 'w-4' : 'w-5')"
        :h="isCollapsed ? 'h-5' : (isSubmenuList ? 'h-4' : 'h-5')"
      />
      <span v-if="!isCollapsed" class="flex-grow truncate">{{ label }}</span>
      <span
        v-if="item.updateMark && !isCollapsed"
        class="flex-none w-2 h-2 rounded-full bg-amber-500"
        aria-hidden="true"
      />
      <span
        v-else-if="item.updateMark && isCollapsed"
        class="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-amber-500"
        aria-hidden="true"
      />
      <icon
        v-if="isExternal && !isCollapsed"
        :path="mdiOpenInNew"
        :size="14"
        w="w-4"
        h="h-4"
        class="flex-none opacity-40"
      />
      <icon
        v-if="hasDropdown && !isCollapsed"
        :path="dropdownIcon"
        :size="18"
        w="w-5"
        h="h-5"
        class="flex-none opacity-60"
      />
    </component>
    <!-- A parent that is also a link keeps showing its children on the icon
         rail. The chevron that toggles the group is hidden at that width, so
         gating them on `isDropdownActive` there left the child route reachable
         only by URL; the rows are icon-only and carry a `title`, so they read
         as an indented continuation of the parent.
         A pure group does not: see `showChildrenWhileCollapsed`. It stays shut
         on the rail and its click widens the sidebar instead.
         Expanded, both behave exactly as before. -->
    <aside-menu-list
      v-if="hasDropdown"
      :menu="item.menu"
      :is-collapsed="isCollapsed"
      :class="childrenVisible ? 'block' : 'hidden'"
      is-submenu-list
      @menu-click="(event, child) => emit('menu-click', event, child)"
    />
  </li>
</template>

<style scoped>
/* `.aside-rail` (css/_translate-rtl.css) sets `left: auto` *after*
   `inset-inline-start`, so in LTR the physical declaration wins the cascade and
   the rail falls back to its static position — on top of the icon. Re-stating
   the logical offset at scoped specificity restores it without breaking the RTL
   mirroring the helper exists for. */
.aside-rail {
  inset-inline-start: -0.5rem;
}

/* A <button> does not inherit the document's font-family — it takes the UA's,
   which on Windows is a 13px Segoe UI that renders visibly narrower than the
   anchors sitting directly above and below it in the same list. Size and weight
   already come from utilities in `linkClass`; only the family needs restating.
   Done here rather than with `font-[inherit]` because Tailwind cannot tell
   whether a bare `font-` arbitrary value means family, size or weight. */
.aside-group-btn {
  font-family: inherit;

  /* A form control does not stretch to its container the way the sibling
     <a class="flex"> rows do — left alone it shrinks to its label (142px
     measured), and `w-full` overshoots to 239px because 100% resolves against
     the <li> and takes no account of the row's own `mx-2`. Subtracting that
     margin lands on 223px, the exact width of every anchor row, so the hover
     tint and the focus ring line up with the rest of the list. */
  width: calc(100% - 1rem);
}
</style>
